import axios from 'axios'
import jwt_decode from 'jwt-decode'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import * as moment from 'moment'
import './AwardsSurveyModal.css'
import { currentSurveyCategories, nextFestivalNominationCategories } from './surveyCategories'
import portraitNominee from '../../assets/images/portraitNominee.png'

const DISABLED_NOMINEES = new Set(['Enis Kqiku', 'Bindi Malsori', 'Jeton Jashari'])

const DEFAULT_NOMINEE_PORTRAIT = portraitNominee
const SHOW_FUTURE_NOMINATIONS_STEP = false

const getNomineeName = (nominee) => (typeof nominee === 'string' ? nominee : nominee?.name || '')
const getNomineeImage = (nominee) => (typeof nominee === 'object' && nominee?.image ? nominee.image : null)
const sortVotableCategoriesFirst = (categories) =>
  categories
    .map((category, index) => ({ category, index }))
    .sort(({ category: a, index: aIndex }, { category: b, index: bIndex }) => {
      const enabledDifference = Number(!!b.enabled) - Number(!!a.enabled)
      return enabledDifference || aIndex - bIndex
    })
    .map(({ category }) => category)

const AwardsSurveyModal = () => {
  const [showModal, setShowModal] = useState(false)
  const [selectedAnswers, setSelectedAnswers] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [currentUserId, setCurrentUserId] = useState(null)
  const [currentStep, setCurrentStep] = useState(0)
  // Searchable dropdown states
  const [searchInputs, setSearchInputs] = useState({})
  const [dropdownOpen, setDropdownOpen] = useState({})
  const [filteredNominees, setFilteredNominees] = useState({})
  const [highlightedIndex, setHighlightedIndex] = useState({})

  const allSteps = useMemo(() => sortVotableCategoriesFirst(currentSurveyCategories).map((c) => ({ ...c, year: 2025 })), [])

  // Get current user ID from token
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      try {
        const decodedToken = jwt_decode(token)
        setCurrentUserId(decodedToken.user_id)
      } catch (error) {
        console.error('Error parsing token:', error)
      }
    }
  }, [])

  useEffect(() => {
    const checkDailySurvey = async () => {
      try {
        const response = await axios.get('/awards-survey/daily-check')
        setShowModal(response.data.showSurvey)
      } catch (error) {
        console.error('Error checking daily survey', error)
      }
    }

    // Check if we're within the survey period (00:01 of 08/09/2025 - 17:00 of 11/09/2025 UTC)
    const now = moment.utc()
    // Start: 00:01 of 08/09/2025 UTC (keep original start time)
    const startDate = moment.utc('2026-06-07 00:01:00')
    // End: 17:00 of 11/09/2025 UTC (updated end time)
    const endDate = moment.utc('2026-06-16 12:59:59')

    // Debug logging
    console.log('=== FRONTEND TIME DEBUG ===')
    console.log('Current UTC time:', now.format('YYYY-MM-DD HH:mm:ss UTC'))
    console.log('Current local time:', moment().format('YYYY-MM-DD HH:mm:ss Z'))
    console.log('Survey start:', startDate.format('YYYY-MM-DD HH:mm:ss UTC'))
    console.log('Survey end:', endDate.format('YYYY-MM-DD HH:mm:ss UTC'))
    console.log('Is within survey period:', now.isBetween(startDate, endDate, null, '[]'))
    console.log('==========================')

    if (now.isBetween(startDate, endDate, null, '[]')) {
      checkDailySurvey()
    }
  }, [])

  const handleNomineeSelect = (categoryId, nominee) => {
    const nomineeName = getNomineeName(nominee) || nominee
    if (DISABLED_NOMINEES.has(nomineeName)) {
      return
    }
    setSelectedAnswers((prev) => ({
      ...prev,
      [categoryId]: nomineeName
    }))
    setSearchInputs((prev) => ({
      ...prev,
      [categoryId]: nomineeName
    }))
    setDropdownOpen((prev) => ({
      ...prev,
      [categoryId]: false
    }))
  }

  const handleSearchInputChange = (categoryId, value, nominees) => {
    setSearchInputs((prev) => ({
      ...prev,
      [categoryId]: value
    }))

    // Filter nominees based on search input
    const filtered = nominees.filter((nominee) => nominee.toLowerCase().includes(value.toLowerCase()))

    setFilteredNominees((prev) => ({
      ...prev,
      [categoryId]: filtered
    }))

    // Open dropdown when typing
    setDropdownOpen((prev) => ({
      ...prev,
      [categoryId]: value.length > 0 || filtered.length > 0
    }))
  }

  const handleSearchInputFocus = (categoryId, nominees) => {
    setDropdownOpen((prev) => ({
      ...prev,
      [categoryId]: true
    }))

    // Initialize filtered nominees if not already set
    if (!filteredNominees[categoryId]) {
      setFilteredNominees((prev) => ({
        ...prev,
        [categoryId]: nominees
      }))
    }
  }

  const handleSearchInputBlur = (categoryId) => {
    // Delay closing to allow for option selection
    setTimeout(() => {
      setDropdownOpen((prev) => ({
        ...prev,
        [categoryId]: false
      }))
    }, 200)
  }

  const clearSearch = (categoryId) => {
    setSearchInputs((prev) => ({
      ...prev,
      [categoryId]: ''
    }))
    setSelectedAnswers((prev) => ({
      ...prev,
      [categoryId]: ''
    }))
    setDropdownOpen((prev) => ({
      ...prev,
      [categoryId]: false
    }))
    setHighlightedIndex((prev) => ({
      ...prev,
      [categoryId]: -1
    }))
  }

  const handleKeyDown = (e, categoryId, nominees) => {
    const currentList = filteredNominees[categoryId] || nominees
    const currentHighlighted = highlightedIndex[categoryId] || -1

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        const nextIndex = currentHighlighted < currentList.length - 1 ? currentHighlighted + 1 : 0
        setHighlightedIndex((prev) => ({
          ...prev,
          [categoryId]: nextIndex
        }))
        break

      case 'ArrowUp':
        e.preventDefault()
        const prevIndex = currentHighlighted > 0 ? currentHighlighted - 1 : currentList.length - 1
        setHighlightedIndex((prev) => ({
          ...prev,
          [categoryId]: prevIndex
        }))
        break

      case 'Enter':
        e.preventDefault()
        if (currentHighlighted >= 0 && currentHighlighted < currentList.length) {
          handleNomineeSelect(categoryId, currentList[currentHighlighted])
        }
        break

      case 'Escape':
        e.preventDefault()
        setDropdownOpen((prev) => ({
          ...prev,
          [categoryId]: false
        }))
        setHighlightedIndex((prev) => ({
          ...prev,
          [categoryId]: -1
        }))
        break

      default:
        break
    }
  }

  const validateEnabledCategories = () => {
    // Check 2025 enabled categories
    const enabled2025 = currentSurveyCategories.filter((cat) => cat.enabled)
    const completed2025 = enabled2025.every((cat) => selectedAnswers[cat.id])

    if (!SHOW_FUTURE_NOMINATIONS_STEP) {
      return enabled2025.length === 0 || completed2025
    }

    // Check 2026 categories with access
    const accessible2026 = nextFestivalNominationCategories.filter((cat) => hasAccessToChoose(cat))
    const completed2026 = accessible2026.every((cat) => selectedAnswers[cat.id])

    // Return true if both sections are complete (or if no accessible categories in a section)
    const is2025Complete = enabled2025.length === 0 || completed2025
    const is2026Complete = accessible2026.length === 0 || completed2026

    return is2025Complete && is2026Complete
  }

  // Check if user has access to choose for 2026 categories
  const hasAccessToChoose = (category) => {
    if (!category.accessToChoose || !currentUserId) return false
    return category.accessToChoose.includes(currentUserId)
  }

  const isCategoryActive = (category) => {
    if (!category) return false
    if (category.year === 2026) {
      return !!category.enabled && hasAccessToChoose(category)
    }
    return !!category.enabled
  }

  const getExplanationText = (category) => {
    if (!category) return ''
    if (category.year === 2025) {
      return category.enabled
        ? '🗳️ Your vote determines the winner!'
        : category.boardSelectionText ||
            `🏢 Winners of this category will be announced by SPEEEX Board at HAJKOFEEEST ${new Date().getFullYear()}`
    }
    const hasAccess = hasAccessToChoose(category)
    if (!category.enabled) {
      return category.boardSelectionText || '🏢 Nominees of this category will be announced by SPEEEX Board at HAJKOFEEEST 2025'
    }
    if (!hasAccess) {
      return '🔒 Restricted voting - Limited to specific team members'
    }
    return '⭐ Nominate outstanding colleagues for future recognition'
  }

  // Render nominees for a category
  const renderNominees = (category, year) => {
    let isEnabled = false
    let explanationText = ''

    if (year === 2025) {
      isEnabled = category.enabled
      explanationText = category.enabled
        ? '🗳️ Your vote determines the winner!'
        : category.boardSelectionText || '🏢 Winners of this category will be announced by SPEEEX Board at HAJKOFEEEST 2025'
    } else if (year === 2026) {
      const hasAccess = hasAccessToChoose(category)
      isEnabled = category.enabled && hasAccess

      if (!category.enabled) {
        // Category is disabled for everyone (company decision)
        explanationText =
          category.boardSelectionText || '🏢 Nominees of this category will be announced by SPEEEX Board at HAJKOFEEEST 2025'
      } else if (!hasAccess) {
        // Category is enabled but user doesn't have access
        explanationText = '🔒 Restricted voting - Limited to specific team members'
      } else {
        // Category is enabled and user has access
        explanationText = '⭐ Nominate outstanding colleagues for future recognition'
      }
    }

    const nominees = [...(category?.nominees || []), 'Abstention']
    return (
      <div>
        {/* Always show explanation text */}
        <div className='category-explanation'>
          <p>{explanationText}</p>
        </div>

        {/* Show nominees based on enabled state */}
        {!isEnabled ? (
          // Disabled state: show nominees but non-interactive (only for 2025)
          year === 2025 && (
            <div className='nominees-list'>
              {category?.nominees?.map((nominee, nomIndex) => (
                <div key={nomIndex} className='nominee-option'>
                  <Label check className='nominee-label disabled'>
                    <Input type='radio' name={category.id} value={nominee} disabled={true} />
                    <span className='nominee-name'>{nominee}</span>
                  </Label>
                </div>
              ))}
            </div>
          )
        ) : // Enabled state: interactive nominees
        category.isDropdown ? (
          <div className='searchable-dropdown-container'>
            <div className='search-input-wrapper'>
              <input
                type='text'
                className='search-input'
                placeholder='Search and select a nominee...'
                value={searchInputs[category.id] || ''}
                onChange={(e) => handleSearchInputChange(category.id, e.target.value, nominees)}
                onFocus={() => handleSearchInputFocus(category.id, nominees)}
                onBlur={() => handleSearchInputBlur(category.id)}
                onKeyDown={(e) => handleKeyDown(e, category.id, nominees)}
              />
              {searchInputs[category.id] && (
                <button type='button' className='clear-search-btn' onClick={() => clearSearch(category.id)} title='Clear selection'>
                  ×
                </button>
              )}
              <div className='search-icon'>
                <i className='fa fa-search'></i>
              </div>
            </div>

            {dropdownOpen[category.id] && (
              <div className='search-dropdown-list'>
                {(filteredNominees[category.id] || nominees).length > 0 ? (
                  (filteredNominees[category.id] || nominees).map((nominee, index) => (
                    <div
                      key={index}
                      className={`search-dropdown-item ${selectedAnswers[category.id] === nominee ? 'selected' : ''} ${
                        (highlightedIndex[category.id] || -1) === index ? 'highlighted' : ''
                      }`}
                      onClick={() => handleNomineeSelect(category.id, nominee)}
                      onMouseEnter={() => setHighlightedIndex((prev) => ({ ...prev, [category.id]: index }))}
                    >
                      {nominee}
                      {selectedAnswers[category.id] === nominee && <i className='fa fa-check selected-icon'></i>}
                    </div>
                  ))
                ) : (
                  <div className='search-dropdown-item no-results'>No nominees found</div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className='nominees-list'>
            {nominees.map((nominee, nomIndex) => (
              <div key={nomIndex} className='nominee-option'>
                <Label check className={`nominee-label ${DISABLED_NOMINEES.has(nominee) ? 'disabled' : ''}`}>
                  <Input
                    type='radio'
                    name={category.id}
                    value={nominee}
                    disabled={DISABLED_NOMINEES.has(nominee)}
                    checked={selectedAnswers[category.id] === nominee}
                    onChange={() => handleNomineeSelect(category.id, nominee)}
                  />
                  <span className='nominee-name'>{nominee}</span>
                </Label>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const handleSubmit = async () => {
    if (!validateEnabledCategories()) {
      const enabled2025Count = currentSurveyCategories.filter((cat) => cat.enabled).length
      const accessible2026Count = SHOW_FUTURE_NOMINATIONS_STEP
        ? nextFestivalNominationCategories.filter((cat) => hasAccessToChoose(cat)).length
        : 0
      const completedCount = Object.keys(selectedAnswers).length

      toast.warning(
        `🎯 Please complete all required sections before submitting.\nProgress: ${completedCount} of ${
          enabled2025Count + accessible2026Count
        } sections completed`,
        {
          position: 'top-center',
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        }
      )
      return
    }

    // Separate current survey answers and legacy future-nominations answers
    const currentSurveyAnswers = {}
    const nextFestivalNominationAnswers = {}

    // Categorize answers
    currentSurveyCategories.forEach((category) => {
      if (selectedAnswers[category.id]) {
        currentSurveyAnswers[category.id] = selectedAnswers[category.id]
      }
    })

    if (SHOW_FUTURE_NOMINATIONS_STEP) {
      nextFestivalNominationCategories.forEach((category) => {
        if (selectedAnswers[category.id]) {
          nextFestivalNominationAnswers[category.id] = selectedAnswers[category.id]
        }
      })
    }

    setIsSubmitting(true)
    try {
      await axios.post('/awards-survey/submit', {
        currentSurveyAnswers,
        nextFestivalNominationAnswers
      })

      // Show success toast
      toast.success('🎉 Thank you! Your responses have been recorded successfully.', {
        position: 'top-right',
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      })

      setShowModal(false)
    } catch (error) {
      console.error('Error submitting survey', error)
      if (error.response?.data?.error) {
        toast.error(`⚠️ ${error.response.data.error}`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        })
      } else {
        toast.error('⚠️ Oops! Something went wrong. Please check your selections and try again.', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // Prevent modal from being closed without completing required categories
  const handleModalClose = (e) => {
    e.preventDefault()
    if (!validateEnabledCategories()) {
      const enabled2025Count = currentSurveyCategories.filter((cat) => cat.enabled).length
      const accessible2026Count = SHOW_FUTURE_NOMINATIONS_STEP
        ? nextFestivalNominationCategories.filter((cat) => hasAccessToChoose(cat)).length
        : 0
      const completedCount = Object.keys(selectedAnswers).length

      toast.warning(
        `🎯 Almost there! Please complete all voting sections to submit your survey.\nProgress: ${completedCount} of ${
          enabled2025Count + accessible2026Count
        } sections completed`,
        {
          position: 'top-center',
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        }
      )
      return false
    }
    return true
  }

  const totalSteps = allSteps.length + (SHOW_FUTURE_NOMINATIONS_STEP ? 1 : 0)
  const isFutureStep = SHOW_FUTURE_NOMINATIONS_STEP && currentStep === allSteps.length
  const currentCategory = isFutureStep ? null : allSteps[currentStep]
  const isLastStep = currentStep === totalSteps - 1
  const activeNow = isCategoryActive(currentCategory)
  const selectionMade = !!(currentCategory && selectedAnswers[currentCategory.id])
  const canProceed = isFutureStep || !activeNow || selectionMade

  const goNext = () => {
    if (!canProceed) {
      toast.warning('🎯 Please select a nominee to continue', {
        position: 'top-center',
        autoClose: 2500,
        hideProgressBar: true
      })
      return
    }
    if (isLastStep) {
      handleSubmit()
      return
    }
    setCurrentStep((s) => Math.min(s + 1, totalSteps - 1))
  }

  const goBack = () => {
    setCurrentStep((s) => Math.max(s - 1, 0))
  }

  const renderNomineeCards = (category) => {
    const nominees = category?.nominees || []
    const interactive = isCategoryActive(category)
    const hasSelection = !!selectedAnswers[category.id]
    return (
      <div className='nominee-cards'>
        {nominees.map((nominee, idx) => {
          const name = getNomineeName(nominee)
          const image = getNomineeImage(nominee) || DEFAULT_NOMINEE_PORTRAIT
          const isDisabledNominee = DISABLED_NOMINEES.has(name)
          const isSelected = selectedAnswers[category.id] === name
          const isCategoryDisabled = category?.enabled === false
          const cardDisabled = isDisabledNominee || !interactive
          const visuallyDisabled = isDisabledNominee || (!interactive && !isCategoryDisabled)
          const isFaded = interactive && hasSelection && !isSelected
          return (
            <button
              type='button'
              key={`${category.id}-${name}-${idx}`}
              className={`nominee-portrait-card ${isSelected ? 'is-selected' : ''} ${visuallyDisabled ? 'is-disabled' : ''} ${
                isFaded ? 'is-faded' : ''
              }`}
              onClick={() => !cardDisabled && handleNomineeSelect(category.id, name)}
              disabled={cardDisabled}
            >
              <div className='nominee-portrait-frame'>
                <img
                  className='nominee-portrait-img'
                  src={image}
                  alt={name}
                  onError={(e) => {
                    e.currentTarget.onerror = null
                    e.currentTarget.src = DEFAULT_NOMINEE_PORTRAIT
                  }}
                />
              </div>
              <div className='nominee-portrait-name'>{name}</div>
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <Modal className='awards-survey-modal modal-xl modal-dialog-centered ' isOpen={!!showModal} backdrop='static' keyboard={false}>
      <ModalBody className='awards-survey-body'>
        {!isFutureStep && currentCategory && (
          <div className='survey-step'>
            <div className='survey-step-intro'>
              <h2 className='survey-step-title'>Excellence Recognition Survey {new Date().getFullYear()}</h2>
              <p className='survey-step-description'>{currentCategory.description}</p>
            </div>

            {!currentCategory.enabled && currentCategory.boardSelectionText && (
              <p className='award-step-board-note'>{currentCategory.boardSelectionText}</p>
            )}

            <h1 className='award-step-title'>{currentCategory.name.toUpperCase()}</h1>

            {/* <div className='award-step-explanation'>
              <span>{getExplanationText(currentCategory)}</span>
            </div> */}

            {activeNow && currentCategory.isDropdown
              ? renderNominees(currentCategory, currentCategory.year)
              : renderNomineeCards(currentCategory)}
          </div>
        )}

        {SHOW_FUTURE_NOMINATIONS_STEP && isFutureStep && (
          <div className='survey-step legacy-2026-section'>
            <h1 className='award-step-title'>🚀 Shape {new Date().getFullYear() + 1} - Nominate Tomorrow's Stars!</h1>
            <p className='award-step-description'>
              Your insights help identify next year's potential award recipients. Nominate colleagues whose contributions deserve
              recognition and celebration at HAJKOFEEEST {new Date().getFullYear()}.
            </p>

            <div className='future-categories-grid'>
              {nextFestivalNominationCategories.map((category) => {
                const userHasAccess = hasAccessToChoose(category)
                const isDisabledForAll = !category.enabled
                const isAccessDenied = category.enabled && !userHasAccess

                let cardClass = 'future-category-card'
                if (isDisabledForAll || isAccessDenied) {
                  cardClass += ' is-disabled-card'
                }

                return (
                  <div key={category.id} className={cardClass}>
                    <div className='future-category-header'>
                      <h4>{category.name}</h4>
                      <p className='future-category-description'>{category.description}</p>
                    </div>
                    {renderNominees(category, 2026)}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter className='awards-survey-footer'>
        <div className='step-footer-content'>
          {currentStep > 0 && (
            <Button color='link' className='step-back-btn' onClick={goBack} disabled={isSubmitting}>
              Back
            </Button>
          )}
          <Button className='continue-btn' onClick={goNext} disabled={(activeNow && !selectionMade) || isSubmitting}>
            {isSubmitting ? (
              <>
                <i className='fa fa-spinner fa-spin' /> Submitting...
              </>
            ) : isLastStep ? (
              <>Submit</>
            ) : (
              <>Continue</>
            )}
          </Button>
          <div className='step-progress'>
            Step {currentStep + 1} of {totalSteps}
          </div>
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default AwardsSurveyModal
